import React, { useState, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faBullseye,
  faTasks,
  faPlus,
  faUser,
  faArrowRight,
  faChevronDown,
  faChevronUp,
  faEdit,
  faTrash,
  faDownload,
  faFilter,
  faXmark,
  faCopy,
  faSearch,
  faSave,
  faTag,
  faEllipsisV,
} from "@fortawesome/free-solid-svg-icons";

/**
 * MSMELLMCaptureHome.jsx
 * - Mobile-first responsive dark UI for managing saved AI items (App name, prompt, response)
 * - Fully functional client-side with dummy data
 */

export default function MSMELLMCaptureHome() {
  // Dummy AI items initial
  const initialItems = [
    {
      id: 1,
      appName: "Retail Insights LLM",
      prompt: "Summarize top 3 customer complaints for kirana stores in last month.",
      response:
        "Top complaints: (1) inconsistent opening hours; (2) stockouts on best sellers; (3) billing discrepancies during peak hours.",
      tags: ["retail", "summary"],
      createdAt: Date.now() - 1000 * 60 * 60 * 24 * 10,
      updatedAt: Date.now() - 1000 * 60 * 60 * 24 * 5,
      notes: "Used for weekly manager brief.",
    },
    {
      id: 2,
      appName: "NPS Analyzer",
      prompt: "Categorize detractor comments into 5-why buckets.",
      response:
        "Categories: Service delays (40%), Product quality (25%), Pricing (15%), Staff behavior (12%), Others (8%).",
      tags: ["nps", "analysis"],
      createdAt: Date.now() - 1000 * 60 * 60 * 24 * 20,
      updatedAt: Date.now() - 1000 * 60 * 60 * 24 * 18,
      notes: "Demo analysis for onboarding.",
    },
    {
      id: 3,
      appName: "Auto-RCA Generator",
      prompt: "Generate 5-why RCA for repeated machine failure in repair shops.",
      response:
        "RCA: 1) Inadequate preventive maintenance → 2) No spare parts inventory → 3) Lack of trained technicians → 4) Overload during peak hours → 5) Poor documentation.",
      tags: ["rca", "mechanic"],
      createdAt: Date.now() - 1000 * 60 * 60 * 24 * 2,
      updatedAt: Date.now() - 1000 * 60 * 60 * 24 * 1,
      notes: "Potential template for mechanincs vertical.",
    },
  ];

  // State
  const [items, setItems] = useState(initialItems);
  const [selectedId, setSelectedId] = useState(items[0]?.id || null);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null => new item

  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent"); // recent | name
  const [toast, setToast] = useState(null);

  // Form state for add/edit
  const emptyForm = { appName: "", prompt: "", response: "", tagsText: "", notes: "" };
  const [form, setForm] = useState(emptyForm);

  // Derived tags list
  const allTags = useMemo(() => {
    const t = new Set();
    items.forEach((it) => (it.tags || []).forEach((tg) => t.add(tg)));
    return Array.from(t);
  }, [items]);

  // Filtered & sorted items
  const displayedItems = useMemo(() => {
    let list = items.slice();
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (it) =>
          it.appName.toLowerCase().includes(q) ||
          it.prompt.toLowerCase().includes(q) ||
          it.response.toLowerCase().includes(q) ||
          (it.notes || "").toLowerCase().includes(q) ||
          (it.tags || []).some((tg) => tg.toLowerCase().includes(q))
      );
    }
    if (tagFilter !== "all") {
      list = list.filter((it) => (it.tags || []).includes(tagFilter));
    }
    if (sortBy === "recent") {
      list.sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt));
    } else if (sortBy === "name") {
      list.sort((a, b) => a.appName.localeCompare(b.appName));
    }
    return list;
  }, [items, search, tagFilter, sortBy]);

  // Select item details when list changes
  useEffect(() => {
    if (!selectedId && displayedItems.length) setSelectedId(displayedItems[0].id);
    if (selectedId && !items.find((it) => it.id === selectedId)) {
      setSelectedId(items[0]?.id || null);
    }
  }, [items, displayedItems, selectedId]);

  // Toast helper
  const showToast = (text, ms = 2500) => {
    setToast(text);
    setTimeout(() => setToast(null), ms);
  };

  // Add new -> open modal
  const startAdd = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  // Edit existing -> prefill form
  const startEdit = (it) => {
    setEditingItem(it);
    setForm({
      appName: it.appName || "",
      prompt: it.prompt || "",
      response: it.response || "",
      tagsText: (it.tags || []).join(", "),
      notes: it.notes || "",
    });
    setShowModal(true);
  };

  // Save (create or update)
  const handleSave = () => {
    const appName = form.appName.trim();
    const prompt = form.prompt.trim();
    const response = form.response.trim();
    const tags = form.tagsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const notes = form.notes.trim();

    if (!appName) {
      return showToast("AI App Name is required");
    }
    if (!prompt) {
      return showToast("AI Prompt is required");
    }

    if (editingItem) {
      // update
      setItems((prev) =>
        prev.map((it) =>
          it.id === editingItem.id
            ? {
                ...it,
                appName,
                prompt,
                response,
                tags,
                notes,
                updatedAt: Date.now(),
              }
            : it
        )
      );
      showToast("Updated AI item");
      setShowModal(false);
    } else {
      // create
      const id = Math.max(0, ...items.map((i) => i.id)) + 1;
      const newItem = {
        id,
        appName,
        prompt,
        response,
        tags,
        notes,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      setItems((prev) => [newItem, ...prev]);
      setSelectedId(id);
      showToast("Saved new AI item");
      setShowModal(false);
    }
  };

  // Delete
  const handleDelete = (id) => {
    if (!confirm("Delete this AI item?")) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
    showToast("Deleted item");
  };

  // Export JSON
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai_items_export_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Export started");
  };

  // Copy JSON
  const copyJSON = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(items, null, 2));
      showToast("Copied JSON to clipboard");
    } catch (e) {
      showToast("Copy failed");
    }
  };

  // Quick duplicate item
  const duplicateItem = (it) => {
    const id = Math.max(0, ...items.map((i) => i.id)) + 1;
    const dup = {
      ...it,
      id,
      appName: `${it.appName} (copy)`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setItems((prev) => [dup, ...prev]);
    showToast("Duplicated item");
  };

  // Selected item
  const selectedItem = items.find((it) => it.id === selectedId) || null;

  // Small UI helpers
  const timeAgo = (t) => {
    if (!t) return "-";
    const diff = Date.now() - t;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days >= 1) return `${days}d ago`;
    const hrs = Math.floor(diff / (1000 * 60 * 60));
    if (hrs >= 1) return `${hrs}h ago`;
    const mins = Math.floor(diff / (1000 * 60));
    return `${mins}m ago`;
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 p-4 sm:p-6">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faTasks} className="text-green-400 text-2xl" />
          <div>
            <h1 className="text-lg sm:text-2xl font-bold">AI Data Capture</h1>
            <div className="text-xs text-gray-400">Saved AI prompts, responses & versions</div>
          </div>
        </div>

        <div className="w-full sm:w-auto grid grid-cols-2 sm:grid-cols-5 gap-2">
          <button
            onClick={startAdd}
            className="bg-blue-600 hover:bg-blue-700 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="hidden sm:inline">Add AI Item</span>
          </button>

          <button
            onClick={exportJSON}
            className="bg-gray-800 hover:bg-gray-700 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
            title="Export all as JSON"
          >
            <FontAwesomeIcon icon={faDownload} />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button
            onClick={copyJSON}
            className="bg-gray-800 hover:bg-gray-700 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
            title="Copy JSON"
          >
            <FontAwesomeIcon icon={faCopy} />
            <span className="hidden sm:inline">Copy</span>
          </button>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-gray-800 text-sm h-10 rounded-lg p-2"
            title="Sort"
          >
            <option value="recent">Sort: Recent</option>
            <option value="name">Sort: Name</option>
          </select>

          <button
            onClick={() => {
              setTagFilter("all");
              setSearch("");
            }}
            className="bg-gray-800 hover:bg-gray-700 py-2 rounded-lg text-sm"
            title="Reset filters"
          >
            Reset
          </button>
        </div>
      </header>

      {/* Main layout: list / detail */}
      <main className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: List + search + filters */}
        <section className="lg:col-span-1 bg-gray-900/40 rounded-2xl p-3 border border-gray-800">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search app, prompt, response, tag..."
                className="w-full p-2 pl-10 bg-gray-800 rounded-md text-sm outline-none"
              />
              <div className="absolute left-3 top-2 text-gray-400">
                <FontAwesomeIcon icon={faSearch} />
              </div>
            </div>

            <div className="w-28">
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="w-full p-2 bg-gray-800 rounded-md h-10 text-sm"
              >
                <option value="all">All Tags</option>
                {allTags.map((tg) => (
                  <option key={tg} value={tg}>
                    {tg}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-xs text-gray-400 mb-2">
            Showing <span className="text-white">{displayedItems.length}</span> items
          </div>

          <div className="space-y-2">
            {displayedItems.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelectedId(it.id)}
                className={`w-full text-left p-3 rounded-lg border ${
                  selectedId === it.id ? "border-blue-600 bg-gray-800" : "border-gray-800 hover:bg-gray-800"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-white">{it.appName}</div>
                    <div className="text-xs text-gray-200 line-clamp-2 mt-1">{it.prompt}</div>
                    <div className="text-xs text-gray-300 mt-1">{(it.tags || []).join(", ")}</div>
                  </div>

                  <div className="text-right text-xs">
                    <div className="text-gray-400">{timeAgo(it.updatedAt || it.createdAt)}</div>
                    <div className="mt-2 flex gap-2 justify-end">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateItem(it);
                        }}
                        className="p-1 rounded bg-gray-800"
                        title="Duplicate"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(it);
                        }}
                        className="p-1 rounded bg-gray-800"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </div>
                  </div>
                </div>
              </button>
            ))}

            {displayedItems.length === 0 && (
              <div className="p-4 text-sm text-gray-400">No items match your filters. Add a new AI item to get started.</div>
            )}
          </div>

          {/* bottom quick actions */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                if (!displayedItems.length) {
                  showToast("No items to export");
                } else {
                  exportJSON();
                }
              }}
              className="flex-1 bg-blue-600 py-2 rounded-md text-sm"
            >
              Export JSON
            </button>

            <button onClick={() => setShowModal(true)} className="w-12 bg-yellow-500 py-2 rounded-md">
              <FontAwesomeIcon icon={faPlus} className="text-black" />
            </button>
          </div>
        </section>

        {/* Right: Details / editor (large) */}
        <section className="lg:col-span-2 bg-gray-900/40 rounded-2xl p-4 border border-gray-800 min-h-[320px]">
          {!selectedItem ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <div className="text-sm">No item selected</div>
              <div className="mt-3">
                <button onClick={startAdd} className="bg-blue-600 py-2 px-3 rounded-md text-sm">
                  Add AI Item
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Meta panel */}
              <div className="lg:col-span-1 bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-gray-400">App</div>
                    <div className="text-lg font-semibold text-white">{selectedItem.appName}</div>
                    <div className="text-xs text-gray-400 mt-1">{selectedItem.notes}</div>

                    <div className="mt-3 text-xs text-gray-400">Tags</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(selectedItem.tags || []).map((tg) => (
                        <div key={tg} className="text-xs px-2 py-1 bg-gray-700 rounded-full">
                          <FontAwesomeIcon icon={faTag} className="mr-1 text-gray-300" />
                          {tg}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-gray-400">Updated</div>
                    <div className="text-sm text-white">{new Date(selectedItem.updatedAt || selectedItem.createdAt).toLocaleString()}</div>

                    <div className="mt-3 flex flex-col gap-2">
                      <button
                        onClick={() => startEdit(selectedItem)}
                        className="py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm flex items-center justify-center gap-2"
                      >
                        <FontAwesomeIcon icon={faEdit} /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(selectedItem.id)}
                        className="py-2 bg-red-700 hover:bg-red-600 rounded text-sm text-white flex items-center justify-center gap-2"
                      >
                        <FontAwesomeIcon icon={faTrash} /> Delete
                      </button>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => duplicateItem(selectedItem)}
                          className="flex-1 py-2 bg-gray-800 rounded text-sm"
                          title="Duplicate"
                        >
                          Duplicate
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard?.writeText(selectedItem.response || "").then(
                              () => showToast("Response copied"),
                              () => showToast("Copy failed")
                            );
                          }}
                          className="py-2 px-3 bg-gray-800 rounded text-sm"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prompt / Response */}
              <div className="lg:col-span-2 space-y-3">
                <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                  <div className="text-xs text-gray-400 mb-2">AI Prompt</div>
                  <div className="text-sm text-white whitespace-pre-wrap">{selectedItem.prompt}</div>
                </div>

                <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-400 mb-2">AI Response</div>
                      <div className="text-sm text-white whitespace-pre-wrap">{selectedItem.response}</div>
                    </div>

                    <div className="text-right text-xs">
                      <div className="text-gray-400">Created</div>
                      <div className="text-sm">{new Date(selectedItem.createdAt).toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                  <div className="text-xs text-gray-400">Notes</div>
                  <div className="text-sm text-gray-300 mt-1">{selectedItem.notes || "—"}</div>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      {/* Modal: Add / Edit */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowModal(false)} />
          <div className="relative w-full max-w-3xl bg-gray-900 rounded-xl border border-gray-800 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={editingItem ? faEdit : faPlus} className="text-blue-400" />
                <h3 className="text-lg font-semibold">{editingItem ? "Edit AI Item" : "Add AI Item"}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    // quick save demo: prefill sample if new and empty
                    if (!editingItem && form.appName.trim() === "") {
                      setForm({
                        appName: "Demo AI App",
                        prompt: "Summarize last week's negative comments for eatery.",
                        response: "Summary: long queues, late delivery, packaging issues.",
                        tagsText: "eatery,summary",
                        notes: "Auto-prefill sample",
                      });
                      showToast("Prefilled sample");
                      return;
                    }
                    setShowModal(false);
                  }}
                  className="px-3 py-2 bg-gray-800 rounded text-sm"
                >
                  Cancel
                </button>
                <button onClick={handleSave} className="px-4 py-2 bg-blue-600 rounded text-sm flex items-center gap-2">
                  <FontAwesomeIcon icon={faSave} />
                  Save
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs text-gray-400">AI App Name</label>
                <input
                  value={form.appName}
                  onChange={(e) => setForm((s) => ({ ...s, appName: e.target.value }))}
                  className="w-full p-2 bg-gray-800 rounded text-sm outline-none"
                />

                <label className="text-xs text-gray-400 mt-2">Tags (comma separated)</label>
                <input
                  value={form.tagsText}
                  onChange={(e) => setForm((s) => ({ ...s, tagsText: e.target.value }))}
                  className="w-full p-2 bg-gray-800 rounded text-sm outline-none"
                  placeholder="e.g. retail, summary"
                />

                <label className="text-xs text-gray-400 mt-2">Notes (optional)</label>
                <input
                  value={form.notes}
                  onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                  className="w-full p-2 bg-gray-800 rounded text-sm outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-gray-400">AI Prompt</label>
                <textarea
                  value={form.prompt}
                  onChange={(e) => setForm((s) => ({ ...s, prompt: e.target.value }))}
                  className="w-full p-2 bg-gray-800 rounded text-sm outline-none h-28"
                />

                <label className="text-xs text-gray-400 mt-2">AI Response</label>
                <textarea
                  value={form.response}
                  onChange={(e) => setForm((s) => ({ ...s, response: e.target.value }))}
                  className="w-full p-2 bg-gray-800 rounded text-sm outline-none h-28"
                />
              </div>
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  // Reset form
                  setForm(emptyForm);
                  setEditingItem(null);
                  showToast("Form cleared");
                }}
                className="px-3 py-2 bg-gray-700 rounded text-sm"
              >
                Clear
              </button>

              <button
                onClick={() => {
                  // preview: quick validation + show toast
                  if (!form.appName.trim()) return showToast("App name required");
                  if (!form.prompt.trim()) return showToast("Prompt required");
                  showToast("Preview ready — click Save to persist");
                }}
                className="px-3 py-2 bg-gray-800 rounded text-sm"
              >
                Preview
              </button>

              <button onClick={handleSave} className="px-4 py-2 bg-blue-600 rounded text-sm flex items-center gap-2">
                <FontAwesomeIcon icon={faSave} />
                Save AI Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed right-4 bottom-6 z-60">
          <div className="bg-black/80 text-white px-4 py-2 rounded shadow">{toast}</div>
        </div>
      )}
    </div>
  );
}
